import { z } from "zod";

// ADV-1347 (lifecycle epic): the shared lifecycle-event contract.
//
// One row per REACHED milestone of an entity, projected by the DB-first view
// `core.entity_lifecycle_v` (slice 1 = job). The view does ALL milestone
// selection in SQL; this schema is the single contract the view, the
// `listEntityLifecycle` service, the `GET /core/entity-lifecycle` route, and
// the apps/web timeline all share — generalizing the opportunity client
// deriver in apps/web/src/lib/lifecycle-events.ts.
//
// Zod-split caveat (agents.md): @adva/types is on Zod v3 for the server.
// apps/web (Zod v4) consumes the inferred TYPE (`LifecycleEvent`), never the
// schema object.

// The entity families the lifecycle view can project. Slice 1 ships `job`;
// `invoice` / `opportunity` are added as the view grows additional UNION ALL
// blocks (proposal later; appointment is non-canonical and intentionally
// omitted).
export const lifecycleEntityTypes = ["job", "opportunity", "invoice"] as const;

export type LifecycleEntityType = (typeof lifecycleEntityTypes)[number];

export const LifecycleEventSchema = z.object({
  entity_type: z.enum(lifecycleEntityTypes).describe("Entity Type"),
  entity_id: z.string().uuid().describe("Entity"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  // The entity-stage the milestone corresponds to (e.g. job: new / scheduled /
  // in_progress / completed). A free-form string at this layer because each
  // entity family has its own disjoint stage enum (ADR-0026).
  stage: z.string().describe("Stage"),
  // The milestone verb (created / scheduled / started / completed / signed_off).
  event_type: z.string().describe("Event Type"),
  title: z.string().describe("Title"),
  // ISO timestamp; the feed is sorted ascending (oldest-first) by this.
  occurred_at: z.string().describe("Occurred At"),
  detail: z.string().nullable().describe("Detail"),
});

export type LifecycleEvent = z.infer<typeof LifecycleEventSchema>;
